import { apiClient } from "./client";
import type { User } from "@/types";

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthResponse<T = unknown> {
  success: boolean;
  data: T;
  message: string;
}

export interface LoginData {
  user: User;
  tokens: AuthTokens;
}

export async function login(
  email: string,
  password: string
): Promise<AuthResponse<LoginData>> {
  const response = await apiClient.post<AuthResponse<LoginData>>(
    "/auth/login",
    { email, password }
  );
  return response.data;
}

export async function getSsoUrl(): Promise<
  AuthResponse<{ url: string; state: string }>
> {
  const response = await apiClient.get<
    AuthResponse<{ url: string; state: string }>
  >("/auth/sso-url");
  return response.data;
}

export async function ssoCallback(
  code: string,
  state: string
): Promise<AuthResponse<LoginData>> {
  const response = await apiClient.post<AuthResponse<LoginData>>(
    "/auth/sso-callback",
    { code, state }
  );
  return response.data;
}

export async function refreshToken(
  refreshToken: string
): Promise<AuthResponse<AuthTokens>> {
  const response = await apiClient.post<AuthResponse<AuthTokens>>(
    "/auth/refresh",
    { refreshToken }
  );
  return response.data;
}

export async function logout(refreshTokenValue: string): Promise<void> {
  await apiClient.post("/auth/logout", { refreshToken: refreshTokenValue });
}

export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<AuthResponse<User>>("/auth/me");
  return response.data.data;
}

export async function recoverPassword(
  email: string
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    `/password-recovery/${email}`
  );
  return response.data;
}

export async function resetPassword(
  token: string,
  newPassword: string
): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(
    "/reset-password/",
    { token, new_password: newPassword }
  );
  return response.data;
}
